import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp, SlideTitle } from "../Deck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { tag: "Doctors", action: "Issue a first clinician order." },
  { tag: "Clinicians", action: "Join the registry; set up PranIQ Pro." },
  { tag: "Patients", action: "Ask your doctor for a PranIQ order." },
  { tag: "Hospitals", action: "Pilot one post-discharge pathway." },
  { tag: "Corporates", action: "Start with one site." },
];

const ROLES = [
  { value: "doctor", label: "Doctor" },
  { value: "clinician", label: "Physiotherapist / Clinician" },
  { value: "patient", label: "Patient" },
  { value: "hospital", label: "Hospital / Clinic" },
  { value: "corporate", label: "Corporate" },
];

const inputCls =
  "w-full rounded-xl border border-line bg-bone px-4 py-3 text-sm text-inktext placeholder:text-ink/35 focus:border-teal focus:ring-2 focus:ring-teal/20 transition-colors";

export default function Slide11Next() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    role: "doctor",
    city: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — we'll call you back shortly.");
      setForm({ name: "", phone: "", role: "doctor", city: "", message: "" });
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid items-start gap-14 lg:grid-cols-[1fr_0.9fr]">
      <div>
        <SlideTitle>
          Start with <em className="italic text-gold">one first step</em>
        </SlideTitle>
        <div className="mt-10">
          {AUDIENCES.map((a, i) => (
            <FadeUp
              key={a.tag}
              delay={0.08 + i * 0.06}
              className="flex items-baseline gap-6 border-t border-ink/10 py-4 last:border-b"
            >
              <span className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-[0.22em] text-teal">
                {a.tag}
              </span>
              <span className="text-base md:text-lg">{a.action}</span>
            </FadeUp>
          ))}
        </div>
        <FadeUp
          delay={0.4}
          className="mt-9 font-display text-xl italic text-gold md:text-2xl"
        >
          Live and delivering in India. Malaysia is next.
        </FadeUp>
      </div>

      <div className="space-y-6">
        <FadeUp delay={0.15} className="rounded-3xl bg-ink p-8 text-bone">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-glow">
            Reach us
          </div>
          <div className="mt-5 space-y-3 text-sm">
            <a
              data-testid="contact-email"
              href="mailto:hello@praniq.life"
              className="block transition-colors hover:text-teal-glow"
            >
              hello@praniq.life
            </a>
            <a
              data-testid="contact-phone-in"
              href="tel:+919600619003"
              className="block transition-colors hover:text-teal-glow"
            >
              +91 96006 19003 <span className="text-mint/50">· India</span>
            </a>
            <a
              data-testid="contact-phone-my"
              href="tel:+60125099967"
              className="block transition-colors hover:text-teal-glow"
            >
              +60 12 509 9967 <span className="text-mint/50">· Malaysia</span>
            </a>
            <a
              data-testid="contact-website"
              href="https://www.praniq.life"
              target="_blank"
              rel="noopener noreferrer"
              className="block transition-colors hover:text-teal-glow"
            >
              www.praniq.life
            </a>
          </div>
        </FadeUp>

        <FadeUp
          delay={0.25}
          className="rounded-3xl border border-line bg-white p-8"
        >
          <h3 className="font-display text-2xl font-medium tracking-tight">
            Request a call back
          </h3>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="callback-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                data-testid="callback-name"
                className={inputCls}
                placeholder="Full name"
                value={form.name}
                onChange={set("name")}
                required
                minLength={2}
              />
              <input
                data-testid="callback-phone"
                className={inputCls}
                type="tel"
                placeholder="Phone"
                value={form.phone}
                onChange={set("phone")}
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <select
                data-testid="callback-role"
                className={inputCls}
                value={form.role}
                onChange={set("role")}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <input
                data-testid="callback-city"
                className={inputCls}
                placeholder="City (optional)"
                value={form.city}
                onChange={set("city")}
              />
            </div>
            <textarea
              data-testid="callback-message"
              className={`${inputCls} min-h-[88px] resize-none`}
              placeholder="Anything we should know? (optional)"
              value={form.message}
              onChange={set("message")}
            />
            <button
              data-testid="callback-submit"
              type="submit"
              disabled={sending}
              className="w-full rounded-full bg-teal py-3.5 text-sm font-medium text-bone transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
            >
              {sending ? "Sending…" : "Request a call back"}
            </button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
